/**
 * AP-6 — zentraler UI-Store: Settings-Persistenz, Filter-/Dekodier-Zustand,
 * Menü-Aktionen (Decoding/View/Filter/Recorder/Help) und deren Status-Texte.
 * Implementiert `AppEventBus` aus `events.ts` (CONTRACTS.md §7).
 *
 * Alle wörtlich zitierten Status-Texte stammen 1:1 aus CanMonitorApp.cpp
 * (Fundstellen in den jeweiligen Methoden-Kommentaren).
 */

import type { AppEventBus, AppEventHandler, AppEventMap, AppEventName } from './events';
import { loadSettings, saveSettings, type Settings } from './settings';
import { setTheme, setUiScalePercent, themeDisplayName, type AppTheme, type UiScalePercent } from '../ui/theme';
import { CanConnection } from './connection';
import { pickTraceToOpen, pickTraceToSave, readTraceText } from './files';
import { packFrames, parseTrace, type TraceFormat } from '../trace';
import {
  defaultFilterState,
  type DecodeFlags,
  type FilterCatalog,
  type FilterState,
  type WorkerEvent,
} from '../worker/protocol';
import type { RxColorMode, ViewKind } from './settings';

/** Nur die vom Store benötigte Teilmenge von `CanConnection` — testbar ohne echten Worker. */
export interface ConnectionLike {
  setFilter(filter: FilterState): Promise<void>;
  setDecodeFlags(flags: DecodeFlags): Promise<void>;
  loadDbc(text: string): Promise<{ messageCount: number }>;
  loadEds(text: string): Promise<{ entryCount: number }>;
  resetDecoding(): Promise<void>;
  resetStats(): Promise<void>;
  close(): Promise<void>;
  recordStart(handle: FileSystemFileHandle, format: TraceFormat): Promise<void>;
  recordStop(): Promise<void>;
  playbackLoad(frames: ArrayBuffer): Promise<{ frameCount: number }>;
  playbackStart(loop: boolean): Promise<void>;
  playbackStop(): Promise<void>;
  on<T extends WorkerEvent['type']>(
    type: T,
    listener: (event: Extract<WorkerEvent, { type: T }>) => void,
  ): () => void;
}

const emptyCatalog: FilterCatalog = { signalNames: [], errorsByCategory: {} };

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export class AppStore implements AppEventBus {
  private readonly listeners = new Map<AppEventName, Set<(payload: unknown) => void>>();
  private readonly connection: ConnectionLike;

  private currentSettings: Settings;
  private currentFilter: FilterState;
  private currentDecodeFlags: DecodeFlags;
  /** Teil von `DecodingActive()`: eine geladene DBC/EDS macht Faerbung sinnvoll. */
  private dbcLoaded = false;
  private edsLoaded = false;
  private currentCatalog: FilterCatalog = emptyCatalog;
  private isRecording = false;
  private isPlaying = false;
  private isLoopEnabled = false;
  /** `adapter_ && adapter_->IsOpen()` im Original — Vorbedingung für Replay. */
  private isConnected = false;

  constructor(connection: ConnectionLike = new CanConnection()) {
    this.connection = connection;
    this.currentSettings = loadSettings();
    this.currentFilter = {
      ...defaultFilterState,
      show11Bit: this.currentSettings.filterShow11Bit,
      show29Bit: this.currentSettings.filterShow29Bit,
    };
    this.currentDecodeFlags = {
      isobusEnabled: this.currentSettings.isobusEnabled,
      canopenEnabled: this.currentSettings.canopenEnabled,
    };
    this.connection.on('catalog', (event) => {
      this.currentCatalog = event.catalog;
      this.emit('catalog:updated', event.catalog);
    });
    this.connection.on('connection', (event) => {
      this.isConnected = event.state === 'connected';
    });
    this.connection.on('playback', (event) => {
      this.isPlaying = event.running;
      this.emit('recorder:changed', {
        recording: this.isRecording,
        playing: this.isPlaying,
        loop: this.isLoopEnabled,
      });
      // `reason` trägt nur das Abschlussereignis (CONTRACTS.md §3.4).
      if (event.reason === 'complete') {
        this.setStatus(`Playback complete (${event.sentCount} frames sent)`);
      } else if (event.reason === 'stopped') {
        this.setStatus(`Playback stopped (${event.sentCount} frames sent)`);
      }
    });
    this.connection.on('error', (event) => {
      // Der Wortlaut gehört in den Store; der Worker meldet nur Code + Ursache.
      if (event.code === 'playback-failed') this.setStatus(`Playback error: ${event.message}`);
      else if (event.code === 'record-failed') this.setStatus(`Record error: ${event.message}`);
    });
  }

  // -- AppEventBus ----------------------------------------------------------

  on<K extends AppEventName>(name: K, handler: AppEventHandler<K>): () => void {
    const set = this.listeners.get(name) ?? new Set();
    set.add(handler as (payload: unknown) => void);
    this.listeners.set(name, set);
    return () => this.off(name, handler);
  }

  off<K extends AppEventName>(name: K, handler: AppEventHandler<K>): void {
    this.listeners.get(name)?.delete(handler as (payload: unknown) => void);
  }

  emit<K extends AppEventName>(name: K, payload: AppEventMap[K]): void {
    const set = this.listeners.get(name);
    if (set === undefined) return;
    for (const handler of set) handler(payload);
  }

  // -- Zustand (nur lesend nach außen) ---------------------------------------

  get settings(): Settings {
    return this.currentSettings;
  }

  get filter(): FilterState {
    return this.currentFilter;
  }

  get decodeFlags(): DecodeFlags {
    return this.currentDecodeFlags;
  }

  get catalog(): FilterCatalog {
    return this.currentCatalog;
  }

  get recording(): boolean {
    return this.isRecording;
  }

  get playing(): boolean {
    return this.isPlaying;
  }

  /** `adapter_ && adapter_->IsOpen()` — Vorbedingung fuer Replay und Senden. */
  get connected(): boolean {
    return this.isConnected;
  }

  get loopEnabled(): boolean {
    return this.isLoopEnabled;
  }

  private setStatus(text: string): void {
    this.emit('status:changed', { text });
  }

  private updateSettings(patch: Partial<Settings>): void {
    const changed = (Object.keys(patch) as (keyof Settings)[]).filter(
      (key) => this.currentSettings[key] !== patch[key],
    );
    if (changed.length === 0) return;
    this.currentSettings = { ...this.currentSettings, ...patch };
    saveSettings(this.currentSettings);
    this.emit('settings:changed', { settings: this.currentSettings, changed });
  }

  private pushFilter(): void {
    this.emit('filter:changed', this.currentFilter);
    void this.connection.setFilter(this.currentFilter);
  }

  private setIdWidthFilter(show11Bit: boolean, show29Bit: boolean): void {
    this.currentFilter = { ...this.currentFilter, show11Bit, show29Bit };
    this.updateSettings({ filterShow11Bit: show11Bit, filterShow29Bit: show29Bit });
    this.pushFilter();
  }

  // -- Filter ⯆ (FEATURE_MATRIX.md §3.3) -------------------------------------

  toggleShow11Bit(): void {
    const value = !this.currentFilter.show11Bit;
    this.currentFilter = { ...this.currentFilter, show11Bit: value };
    this.updateSettings({ filterShow11Bit: value });
    this.pushFilter();
  }

  toggleShow29Bit(): void {
    const value = !this.currentFilter.show29Bit;
    this.currentFilter = { ...this.currentFilter, show29Bit: value };
    this.updateSettings({ filterShow29Bit: value });
    this.pushFilter();
  }

  applyMaskFilter(mask: number, maskId: number): void {
    this.currentFilter = { ...this.currentFilter, contentFilterMode: 'mask', mask, maskId };
    this.pushFilter();
    this.setStatus('Mask filter applied');
  }

  /** Hex-Parse-Fehler im Dialog (CanMonitorApp.cpp:2506, wörtlich). */
  invalidMaskFilter(): void {
    this.setStatus('Filter error: invalid mask/ID');
  }

  clearContentFilter(): void {
    this.currentFilter = { ...this.currentFilter, contentFilterMode: 'none' };
    this.pushFilter();
    this.setStatus('Content filter cleared');
  }

  applyRangeFilter(min: number, max: number): void {
    this.currentFilter = { ...this.currentFilter, rangeEnabled: true, rangeMin: min, rangeMax: max };
    this.pushFilter();
    this.setStatus('Range filter applied');
  }

  clearRangeFilter(): void {
    this.currentFilter = { ...this.currentFilter, rangeEnabled: false };
    this.pushFilter();
    this.setStatus('Range filter cleared');
  }

  applyErrorFrameFilter(checkedKeys: readonly string[]): void {
    const categories = checkedKeys.filter((key) => key in this.currentCatalog.errorsByCategory);
    const specific = checkedKeys.filter((key) => !(key in this.currentCatalog.errorsByCategory));
    this.currentFilter = {
      ...this.currentFilter,
      errorFilterEnabled: checkedKeys.length > 0,
      errorCategories: categories,
      errorSpecific: specific,
    };
    this.pushFilter();
    this.setStatus(
      this.currentFilter.errorFilterEnabled
        ? 'Error frame filter applied'
        : 'Error frame filter cleared (nothing selected)',
    );
  }

  clearErrorFrameFilter(): void {
    this.currentFilter = { ...this.currentFilter, errorFilterEnabled: false, errorCategories: [], errorSpecific: [] };
    this.pushFilter();
    this.setStatus('Error frame filter cleared');
  }

  applySignalFilter(checkedKeys: readonly string[]): void {
    this.currentFilter = {
      ...this.currentFilter,
      signalFilterEnabled: checkedKeys.length > 0,
      signalNames: [...checkedKeys],
    };
    this.pushFilter();
    this.setStatus(
      this.currentFilter.signalFilterEnabled ? 'Signal filter applied' : 'Signal filter cleared (nothing selected)',
    );
  }

  clearSignalFilter(): void {
    this.currentFilter = { ...this.currentFilter, signalFilterEnabled: false, signalNames: [] };
    this.pushFilter();
    this.setStatus('Signal filter cleared');
  }

  /**
   * `Clear Filters` (FM-MEN-22). **Korrigiert gegenüber FEATURE_MATRIX.md**:
   * das Original (`kMenuFilterClear`-Handler, CanMonitorApp.cpp:540) ruft nur
   * `OnFilterClear()` + `OnFilterClearRange()` auf — Fehler-/Signalfilter
   * bleiben unangetastet, die ID-Breitenfilter sowieso. Siehe PROGRESS.md.
   */
  clearFilters(): void {
    this.clearContentFilter();
    this.clearRangeFilter();
  }

  // -- Decoding ⯆ (FEATURE_MATRIX.md §3.1) -----------------------------------

  toggleIsobusDecoding(): void {
    const isobusEnabled = !this.currentDecodeFlags.isobusEnabled;
    this.currentDecodeFlags = { ...this.currentDecodeFlags, isobusEnabled };
    this.updateSettings({ isobusEnabled });
    void this.connection.setDecodeFlags(this.currentDecodeFlags);
    this.emitDecodeChanged();
    this.setStatus(isobusEnabled ? 'ISOBUS decoding activated' : 'ISOBUS decoding deactivated');
  }

  toggleCanopenDecoding(): void {
    const canopenEnabled = !this.currentDecodeFlags.canopenEnabled;
    this.currentDecodeFlags = { ...this.currentDecodeFlags, canopenEnabled };
    this.updateSettings({ canopenEnabled });
    void this.connection.setDecodeFlags(this.currentDecodeFlags);
    this.emitDecodeChanged();
    this.setStatus(canopenEnabled ? 'CANopen decoding activated' : 'CANopen decoding deactivated');
  }

  private emitDecodeChanged(): void {
    this.emit('decode:changed', {
      flags: this.currentDecodeFlags,
      dbcLoaded: this.dbcLoaded,
      edsLoaded: this.edsLoaded,
    });
  }

  async loadDbc(fileName: string, text: string): Promise<void> {
    try {
      const result = await this.connection.loadDbc(text);
      this.dbcLoaded = true;
      this.emitDecodeChanged();
      this.setStatus(`DBC loaded: ${fileName} (${result.messageCount} messages)`);
    } catch (error) {
      this.setStatus(`DBC load failed: ${errorMessage(error)}`);
    }
  }

  dbcLoadCanceled(): void {
    this.setStatus('DBC load canceled');
  }

  async loadEds(fileName: string, text: string): Promise<void> {
    try {
      const result = await this.connection.loadEds(text);
      this.edsLoaded = true;
      this.currentDecodeFlags = { ...this.currentDecodeFlags, canopenEnabled: true };
      this.updateSettings({ canopenEnabled: true });
      this.emitDecodeChanged();
      this.setStatus(`EDS loaded: ${fileName} (${result.entryCount} objects) -- CANopen decoding activated`);
    } catch (error) {
      this.setStatus(`EDS load failed: ${errorMessage(error)}`);
    }
  }

  edsLoadCanceled(): void {
    this.setStatus('EDS load canceled');
  }

  async resetDecoding(): Promise<void> {
    try {
      await this.connection.resetDecoding();
      this.dbcLoaded = false;
      this.edsLoaded = false;
      this.currentDecodeFlags = { isobusEnabled: false, canopenEnabled: false };
      this.updateSettings({ isobusEnabled: false, canopenEnabled: false });
      this.emitDecodeChanged();
      this.setStatus('Decoding reset');
    } catch (error) {
      this.setStatus(`Reset failed: ${errorMessage(error)}`);
    }
  }

  /** Öffnet ein (noch nicht in jedem AP existierendes) Fenster/eine View. */
  openView(kind: ViewKind, embedded = false): void {
    this.emit('view:opened', { kind, embedded });
  }

  openDm1Monitor(): void {
    this.setIdWidthFilter(false, true); // DM1 ist 29-Bit-only (FM-MEN-03)
    this.openView('dm1', this.currentSettings.singleWindowMode);
  }

  openAddressClaim(): void {
    this.setIdWidthFilter(false, true); // FM-MEN-04
    this.openView('address-claim', this.currentSettings.singleWindowMode);
  }

  openIsobusEventLog(): void {
    this.setIdWidthFilter(false, true); // FM-MEN-11
    this.openView('isobus-event-log', this.currentSettings.singleWindowMode);
  }

  openNodeScan(): void {
    this.setIdWidthFilter(true, false); // CANopen-SDO ist 11-Bit (FM-MEN-08)
    this.openView('node-scan', this.currentSettings.singleWindowMode);
  }

  // -- View ⯆ (FEATURE_MATRIX.md §3.2) ---------------------------------------

  setViewMode(mode: Settings['viewMode']): void {
    this.updateSettings({ viewMode: mode });
    this.emit('viewmode:changed', { mode });
  }

  /** `ClearTable()`: Tabelle, Sequenznummer, Zaehler und Buslast auf 0. */
  clearTable(): void {
    void this.connection.resetStats();
    this.emit('frames:cleared', {});
  }

  // -- Recorder (FEATURE_MATRIX.md §3.4) -------------------------------------

  /**
   * `OnRecordClicked` (CanMonitorApp.cpp:2012). Der Worker schreibt mit, weil
   * nur er die Frames vor den Anzeigefiltern sieht; der Dateidialog muss aber
   * im Main-Thread laufen.
   */
  async toggleRecording(): Promise<void> {
    if (this.isRecording) {
      try {
        await this.connection.recordStop();
      } catch (error) {
        this.setStatus(`Record error: ${errorMessage(error)}`);
      }
      this.isRecording = false;
      this.emitRecorderChanged();
      this.setStatus('Recording stopped');
      return;
    }
    if (this.isPlaying) {
      this.setStatus('Record blocked: Replay is currently running');
      return;
    }

    let picked;
    try {
      picked = await pickTraceToSave();
    } catch (error) {
      this.setStatus(`Record error: ${errorMessage(error)}`);
      return;
    }
    if (picked === null) {
      this.setStatus('Record canceled');
      return;
    }

    try {
      await this.connection.recordStart(picked.handle, picked.format);
    } catch (error) {
      this.setStatus(`Record error: ${errorMessage(error)}`);
      return;
    }
    this.isRecording = true;
    this.emitRecorderChanged();
    // Das Original zeigt den vollen Pfad; der Browser gibt nur den Dateinamen her.
    this.setStatus(`Recording: ${picked.name}`);
  }

  /** `OnPlayClicked` (CanMonitorApp.cpp:2051) bzw. `OnStopClicked` beim zweiten Klick. */
  async togglePlayback(): Promise<void> {
    if (this.isPlaying) {
      // Wie `OnStopClicked`: nur die Abbruchanforderung setzen — der Zustand
      // wechselt erst, wenn der Worker das Abschlussereignis schickt.
      await this.connection.playbackStop();
      return;
    }
    if (this.isRecording) {
      this.setStatus('Replay blocked: Record is currently running');
      return;
    }
    if (!this.currentSettings.simulationMode && !this.isConnected) {
      this.setStatus('Playback blocked: connect first');
      return;
    }

    let picked;
    try {
      picked = await pickTraceToOpen();
    } catch (error) {
      this.setStatus(`Playback error: ${errorMessage(error)}`);
      return;
    }
    if (picked === null) {
      this.setStatus('Playback canceled');
      return;
    }

    try {
      const frames = parseTrace(await readTraceText(picked.handle), picked.format);
      await this.connection.playbackLoad(packFrames(frames));
      await this.connection.playbackStart(this.isLoopEnabled);
    } catch (error) {
      this.setStatus(`Playback error: ${errorMessage(error)}`);
      return;
    }
    this.setStatus(`Playback started: ${picked.name}`);
  }

  toggleLoop(): void {
    this.isLoopEnabled = !this.isLoopEnabled;
    this.emitRecorderChanged();
  }

  private emitRecorderChanged(): void {
    this.emit('recorder:changed', {
      recording: this.isRecording,
      playing: this.isPlaying,
      loop: this.isLoopEnabled,
    });
  }

  // -- Help ⯆ (FEATURE_MATRIX.md §3.5) ----------------------------------------

  setUiScale(percent: UiScalePercent): void {
    setUiScalePercent(percent);
    this.updateSettings({ uiScalePercent: percent });
    this.emit('scale:changed', { percent });
    this.setStatus(`UI size scaling: ${percent}%`);
  }

  setFadeMs(ms: number): void {
    this.updateSettings({ rxFadeMs: ms });
    this.setStatus(ms === 0 ? 'Rx match color: Static (no fade)' : `Rx match color: Blink (fade ${ms} ms)`);
  }

  /** Nach dem Ziehen einer Spaltenkante in der Frametabelle (AP-5). */
  setColumnWidths(widths: readonly number[]): void {
    // Kein updateSettings(): dessen Vergleich ist flach, ein Array ist damit
    // immer "ungleich" und loeste bei jedem Zug ein settings:changed aus.
    this.currentSettings = { ...this.currentSettings, columnWidths: [...widths] };
    saveSettings(this.currentSettings);
  }

  /** AP-12: nach dem Ziehen des Hauptteilers (`mainSplitterRatio_`). */
  setSplitterRatio(ratio: number): void {
    this.updateSettings({ splitterRatio: ratio });
  }

  /** FM-MEN-30/31: erneuter Klick auf denselben Modus schaltet zurück auf `whole-row`. */
  setColorMode(mode: Extract<RxColorMode, 'dots' | 'no-colors'>): void {
    const next: RxColorMode = this.currentSettings.rxColorMode === mode ? 'whole-row' : mode;
    this.updateSettings({ rxColorMode: next });
    const label = next === 'dots' ? 'Dots' : next === 'no-colors' ? 'No Colors' : 'Whole Row';
    this.setStatus(`Rx match color mode: ${label}`);
  }

  setTheme(theme: AppTheme): void {
    setTheme(theme);
    this.updateSettings({ theme });
    this.emit('theme:changed', { theme });
    this.setStatus(`${themeDisplayName(theme)} Theme enabled`);
  }

  toggleSingleWindowMode(): void {
    const value = !this.currentSettings.singleWindowMode;
    this.updateSettings({ singleWindowMode: value });
    this.setStatus(
      value ? 'Single Window Mode enabled -- open a view to dock it below' : 'Single Window Mode disabled',
    );
  }

  toggleSimulationMode(): void {
    const value = !this.currentSettings.simulationMode;
    this.updateSettings({ simulationMode: value });
    if (value) void this.connection.close();
    this.setStatus(
      value
        ? 'Simulation Mode enabled -- adapter disconnected; Replay/TX/Engine Sim work without CAN hardware'
        : 'Simulation Mode disabled -- Replay/TX/Engine Sim require a real connection again',
    );
  }
}
